import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InventoryItem, MoveForm, LossForm, Location, Vendor, DailySummaryItem, Site } from '../types/interfaces';
import { fetchDailySummary } from '../utils/fetchUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { buildSplatPath } from '../utils/routeParams';
import { centralToday } from '../utils/centralDate';
import {
  filterInventoryList,
  inventoryRecordSearch,
  inventoryRowKey,
  readShowZeroStock,
  writeShowZeroStock,
} from '../utils/inventoryStock';

const OUR_DSP = 'DSP-AL-20010';

interface InventoryProps {
  vendors: Vendor[];
  refreshVendors: () => Promise<void>;
  inventory: InventoryItem[];
  refreshInventory: () => Promise<void>;
}

const Inventory: React.FC<InventoryProps> = ({ inventory, refreshInventory, vendors, refreshVendors }) => {
  const [dailySummary, setDailySummary] = useState<DailySummaryItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [moveForm, setMoveForm] = useState<MoveForm>({ identifier: '', toAccount: 'Storage', proofGallons: '' });
  const [lossForm, setLossForm] = useState<LossForm>({
    identifier: '',
    quantityLost: '',
    proofGallonsLost: '',
    reason: '',
    date: centralToday(),
    siteId: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showZeroStock, setShowZeroStock] = useState(readShowZeroStock);
  const [zeroExtras, setZeroExtras] = useState<InventoryItem[]>([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showLossModal, setShowLossModal] = useState(false);
  const [productionError, setProductionError] = useState<string | null>(null);
  /** True only for the first page load (skeleton). Refresh does not blank the table. */
  const [initialLoading, setInitialLoading] = useState(true);

  const navigate = useNavigate();

  // Single boot fetch: inventory + sites + all locations + daily summary.
  // Previously three useEffects raced isLoading and the sites/locations effect
  // re-ran whenever `inventory` changed after refreshInventory — causing ~3 flashes.
  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    const boot = async () => {
      setInitialLoading(true);
      setProductionError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const [sitesRes, locsRes, summaryResult, inventoryErr] = await Promise.all([
          apiFetch(`${API_BASE_URL}/api/sites`, {
            headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
            signal,
          }),
          apiFetch(`${API_BASE_URL}/api/locations`, {
            headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
            signal,
          }),
          fetchDailySummary().catch((err: unknown) => {
            console.error('[Inventory] Daily summary error:', err);
            return [] as DailySummaryItem[];
          }),
          refreshInventory()
            .then(() => null as string | null)
            .catch((err: unknown) => {
              console.error('[Inventory] refreshInventory error:', err);
              return err instanceof Error ? err.message : 'Failed to refresh inventory';
            }),
        ]);

        if (signal.aborted) return;

        if (!sitesRes.ok) {
          throw new Error(`Sites fetch failed: HTTP ${sitesRes.status}`);
        }
        if (!locsRes.ok) {
          throw new Error(`Locations fetch failed: HTTP ${locsRes.status}`);
        }

        const sitesData: Site[] = await sitesRes.json();
        const locsData: Location[] = await locsRes.json();
        if (signal.aborted) return;

        setSites(Array.isArray(sitesData) ? sitesData : []);
        setLocations(Array.isArray(locsData) ? locsData : []);
        setDailySummary(Array.isArray(summaryResult) ? summaryResult : []);
        if (inventoryErr) {
          setProductionError(`Failed to refresh inventory: ${inventoryErr}`);
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('[Inventory] Boot fetch error:', err);
        setProductionError(
          err instanceof Error ? `Failed to load inventory page: ${err.message}` : 'Failed to load inventory page'
        );
      } finally {
        if (!signal.aborted) setInitialLoading(false);
      }
    };

    void boot();
    return () => abortController.abort();
    // Mount-only: refreshInventory is stable (useCallback [] in App).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional single boot
  }, []);

  // Ledger locations at 0 On Hand are not inventory rows. Load them only while
  // the toggle is on (remembered in localStorage).
  useEffect(() => {
    if (!showZeroStock) return undefined;
    const abortController = new AbortController();
    const { signal } = abortController;
    const loadZeros = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await apiFetch(`${API_BASE_URL}/api/inventory?includeZero=1&extrasOnly=1`, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token || ''}` },
          signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: InventoryItem[] = await res.json();
        if (!signal.aborted) setZeroExtras(Array.isArray(data) ? data : []);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('[Inventory] Zero-stock load error:', err);
        if (!signal.aborted) {
          setProductionError(
            err instanceof Error ? `Failed to load zero-stock items: ${err.message}` : 'Failed to load zero-stock items'
          );
        }
      }
    };
    void loadZeros();
    return () => abortController.abort();
  }, [showZeroStock]);

  const getLocationName = (locationId: number | undefined) => {
    if (!locationId) return 'Unknown Location';
    const location = locations.find(loc => loc?.locationId === locationId);
    return location?.name || 'Unknown Location';
  };

  const getSiteName = (siteId: string | undefined) => {
    if (!siteId) return 'Unknown Site';
    const site = sites.find(site => site?.siteId === siteId);
    return site?.name || 'Unknown Site';
  };

  const handleMove = async () => {
    if (!moveForm.identifier || !moveForm.toAccount || !moveForm.proofGallons) {
      setProductionError('Please fill in Identifier, To Account, and Proof Gallons.');
      return;
    }
    const parsedProofGallons = parseFloat(moveForm.proofGallons);
    if (isNaN(parsedProofGallons) || parsedProofGallons <= 0) {
      setProductionError('Proof Gallons must be a positive number.');
      return;
    }
    try {
      console.log('[Inventory] Moving item:', moveForm);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('[Inventory] No token found, redirecting to login');
        navigate('/login');
        throw new Error('No token found in localStorage');
      }
      const res = await apiFetch(`${API_BASE_URL}/api/inventory/move`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...moveForm, proofGallons: parsedProofGallons }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Move failed: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      await refreshInventory();
      setMoveForm({ identifier: '', toAccount: 'Storage', proofGallons: '' });
      setShowMoveModal(false);
      setProductionError(null);
      console.log('[Inventory] Move successful');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setProductionError(`Failed to move item: ${errorMessage}`);
      console.error('[Inventory] Move error:', err);
    }
  };

  const handleRecordLoss = async () => {
    const matchedItem = inventory.find((i) => i?.identifier === lossForm.identifier);
    const siteId = lossForm.siteId || matchedItem?.siteId || '';
    if (!lossForm.identifier || !lossForm.quantityLost || !lossForm.reason || !siteId) {
      setProductionError('Please fill in Identifier, Quantity Lost, Reason, and Site.');
      return;
    }
    try {
      const payload = { ...lossForm, siteId, dspNumber: OUR_DSP };
      console.log('[Inventory] Recording loss:', payload);
      const res = await apiFetch(`${API_BASE_URL}/api/inventory/loss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Record loss failed: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      await refreshInventory();
      setLossForm({
        identifier: '',
        quantityLost: '',
        proofGallonsLost: '',
        reason: '',
        date: centralToday(),
        siteId: '',
      });
      setShowLossModal(false);
      setProductionError(null);
      console.log('[Inventory] Loss recorded');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setProductionError(`Failed to record loss: ${errorMessage}`);
      console.error('[Inventory] Record loss error:', err);
    }
  };

  const getIdentifier = (item: InventoryItem) => item?.identifier || 'N/A';

  const handleItemClick = (item: InventoryItem) => {
    // Splat-friendly path so identifiers with `/` (e.g. 1/6 BBL Keg) still match.
    // site, location, and account select this On Hand row (Madison vs Athens).
    const path = buildSplatPath('/inventory', getIdentifier(item));
    const search = inventoryRecordSearch(item);
    const target = search ? `${path}?${search}` : path;
    console.log('[Inventory] Navigating to:', target);
    navigate(target);
  };

  const toggleZeroStock = () => {
    setShowZeroStock((prev) => {
      const next = !prev;
      writeShowZeroStock(next);
      return next;
    });
  };

  const listSource = React.useMemo(() => {
    if (!showZeroStock || zeroExtras.length === 0) return inventory;
    const seen = new Set(inventory.map((item) => inventoryRowKey(item)));
    const extra = zeroExtras.filter((item) => !seen.has(inventoryRowKey(item)));
    return extra.length ? [...inventory, ...extra] : inventory;
  }, [inventory, showZeroStock, zeroExtras]);

  const filteredInventory = React.useMemo(
    () =>
      filterInventoryList(listSource, searchTerm, showZeroStock, (item) => {
        if (!item.locationId) return '';
        const location = locations.find((loc) => loc?.locationId === item.locationId);
        return location?.name || '';
      }),
    [listSource, searchTerm, showZeroStock, locations]
  );

  return (
    <div className="page-container container" data-testid="inventory-page">
      <h2 className="app-header mb-4">Inventory Management</h2>
      {productionError && (
        <div className="alert alert-danger mb-3">{productionError}</div>
      )}
      {initialLoading && (
        <div className="alert alert-info mb-3" data-testid="inventory-skeleton">
          Loading inventory…
        </div>
      )}
      {!initialLoading && !filteredInventory.length && !productionError && (
        <div className="alert alert-warning mb-3">No inventory items found.</div>
      )}
      <div className="inventory-actions mb-4 d-flex gap-2 flex-wrap">
        <button className="btn btn-primary" onClick={() => navigate('/receive')}>
          Receive Inventory
        </button>
        <button className="btn btn-primary" onClick={() => setShowMoveModal(true)}>
          Move Item
        </button>
        <button className="btn btn-primary" onClick={() => setShowLossModal(true)}>
          Record Loss
        </button>
      </div>

      <div className="inventory-toolbar mb-3">
        <input
          type="text"
          placeholder="Search by item, lot, or location"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="form-control"
          aria-label="Search inventory"
        />
        <button
          type="button"
          className={`btn btn-secondary inventory-zero-toggle${showZeroStock ? ' is-on' : ''}`}
          aria-pressed={showZeroStock}
          data-testid="show-zero-stock"
          onClick={toggleZeroStock}
        >
          Show zero stock
        </button>
      </div>

      <h3 className="app-header mb-3">Inventory</h3>
      <div className="inventory-table-container">
        {initialLoading ? (
          <div className="alert alert-secondary text-center" data-testid="inventory-table-skeleton">
            Loading inventory…
          </div>
        ) : filteredInventory.length > 0 ? (
          <>
            <table className="inventory-table table table-striped">
              <thead>
                <tr>
                  <th>Item-Lot</th>
                  <th>Type</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Received Date</th>
                  <th>Location</th>
                  <th>Site</th>
                  <th>Account</th>
                  <th>Source</th>
                  <th>Description</th>
                  <th>Unit Cost</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.map((item) => (
                  <tr key={inventoryRowKey(item)} onClick={() => handleItemClick(item)} style={{ cursor: 'pointer' }}>
                    <td>{getIdentifier(item)}</td>
                    <td>{item?.type || 'N/A'}</td>
                    <td>{item?.quantity ?? 'N/A'}</td>
                    <td>{item?.unit || 'N/A'}</td>
                    <td>{item?.receivedDate || 'N/A'}</td>
                    <td>{getLocationName(item?.locationId)}</td>
                    <td>{getSiteName(item?.siteId)}</td>
                    <td>{item?.account || 'N/A'}</td>
                    <td>{item?.source || 'Unknown'}</td>
                    <td>{item?.description || 'N/A'}</td>
                    <td>{item?.cost || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="card-list">
              {filteredInventory.map((item) => (
                <div key={inventoryRowKey(item)} className="card-item card mb-2" onClick={() => handleItemClick(item)} style={{ cursor: 'pointer' }}>
                  <div className="card-body">
                    <p className="card-text"><strong>Item-Lot:</strong> {getIdentifier(item)}</p>
                    <p className="card-text"><strong>Type:</strong> {item?.type || 'N/A'}</p>
                    <p className="card-text"><strong>Quantity:</strong> {item?.quantity ?? 'N/A'}</p>
                    <p className="card-text"><strong>Unit:</strong> {item?.unit || 'N/A'}</p>
                    <p className="card-text"><strong>Received Date:</strong> {item?.receivedDate || 'N/A'}</p>
                    <p className="card-text"><strong>Location:</strong> {getLocationName(item?.locationId)}</p>
                    <p className="card-text"><strong>Site:</strong> {getSiteName(item?.siteId)}</p>
                    <p className="card-text"><strong>Account:</strong> {item?.account || 'N/A'}</p>
                    <p className="card-text"><strong>Source:</strong> {item?.source || 'Unknown'}</p>
                    <p className="card-text"><strong>Description:</strong> {item?.description || 'N/A'}</p>
                    <p className="card-text"><strong>Unit Cost:</strong> {item?.cost || 'N/A'}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="alert alert-info text-center">No inventory items found.</div>
        )}
      </div>

      <h3 className="app-header mb-3">Daily Summary</h3>
      <div className="inventory-table-container">
        {initialLoading ? (
          <div className="alert alert-secondary text-center" data-testid="daily-summary-skeleton">
            Loading daily summary…
          </div>
        ) : dailySummary.length > 0 ? (
          <>
            <table className="inventory-table table table-striped">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Total Proof Gallons</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {dailySummary.map((item, index) => (
                  <tr key={index}>
                    <td>{item?.date || 'N/A'}</td>
                    <td>{item?.account || 'N/A'}</td>
                    <td>{item?.type || 'N/A'}</td>
                    <td>{item?.totalProofGallons ? parseFloat(item.totalProofGallons.toString()).toFixed(2) : 'N/A'}</td>
                    <td>{getLocationName(item?.locationId)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="card-list">
              {dailySummary.map((item, index) => (
                <div key={index} className="card-item card mb-2">
                  <div className="card-body">
                    <p className="card-text"><strong>Date:</strong> {item?.date || 'N/A'}</p>
                    <p className="card-text"><strong>Account:</strong> {item?.account || 'N/A'}</p>
                    <p className="card-text"><strong>Type:</strong> {item?.type || 'N/A'}</p>
                    <p className="card-text"><strong>Total Proof Gallons:</strong> {item?.totalProofGallons ? parseFloat(item.totalProofGallons.toString()).toFixed(2) : 'N/A'}</p>
                    <p className="card-text"><strong>Location:</strong> {getLocationName(item?.locationId)}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="alert alert-info text-center">No daily summary data available.</div>
        )}
      </div>

      {showMoveModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}>
          <div className="modal-dialog modal-content" style={{ maxWidth: '400px', margin: '0 auto', backgroundColor: '#FFFFFF' }}>
            <div className="modal-header">
              <h5 className="modal-title" style={{ color: '#555555' }}>Move Inventory</h5>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Item-Lot (e.g., Flaked Corn)"
                value={moveForm.identifier}
                onChange={e => setMoveForm({ ...moveForm, identifier: e.target.value })}
                className="form-control mb-2"
              />
              <select
                value={moveForm.toAccount}
                onChange={e => setMoveForm({ ...moveForm, toAccount: e.target.value })}
                className="form-control mb-2"
              >
                <option value="Storage">Storage</option>
                <option value="Processing">Processing</option>
                <option value="Production">Production</option>
              </select>
              <input
                type="number"
                placeholder="Proof Gallons"
                value={moveForm.proofGallons}
                onChange={e => setMoveForm({ ...moveForm, proofGallons: e.target.value })}
                className="form-control mb-2"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleMove}>
                Move
              </button>
              <button className="btn btn-danger" onClick={() => setShowMoveModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showLossModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}>
          <div className="modal-dialog modal-content" style={{ maxWidth: '400px', margin: '0 auto', backgroundColor: '#FFFFFF' }}>
            <div className="modal-header">
              <h5 className="modal-title" style={{ color: '#555555' }}>Record Loss</h5>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Item-Lot (e.g., Flaked Corn)"
                value={lossForm.identifier}
                onChange={e => {
                  const identifier = e.target.value;
                  const matched = inventory.find((i) => i?.identifier === identifier);
                  setLossForm({
                    ...lossForm,
                    identifier,
                    siteId: matched?.siteId || lossForm.siteId,
                  });
                }}
                className="form-control mb-2"
              />
              <select
                value={lossForm.siteId}
                onChange={e => setLossForm({ ...lossForm, siteId: e.target.value })}
                className="form-control mb-2"
              >
                <option value="">Select Site</option>
                {sites.map((site) => (
                  <option key={site.siteId} value={site.siteId}>
                    {site.name} ({site.siteId})
                  </option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Quantity Lost"
                value={lossForm.quantityLost}
                onChange={e => setLossForm({ ...lossForm, quantityLost: e.target.value })}
                className="form-control mb-2"
              />
              <input
                type="number"
                placeholder="Proof Gallons Lost"
                value={lossForm.proofGallonsLost}
                onChange={e => setLossForm({ ...lossForm, proofGallonsLost: e.target.value })}
                className="form-control mb-2"
              />
              <input
                type="text"
                placeholder="Reason for Loss"
                value={lossForm.reason}
                onChange={e => setLossForm({ ...lossForm, reason: e.target.value })}
                className="form-control mb-2"
              />
              <input
                type="date"
                value={lossForm.date}
                onChange={e => setLossForm({ ...lossForm, date: e.target.value })}
                className="form-control mb-2"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleRecordLoss}>
                Record Loss
              </button>
              <button className="btn btn-danger" onClick={() => setShowLossModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;